import React, { useState, useEffect, lazy, Suspense } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { UserProvider, useUser } from "./providers/UserContext";
import { GameProvider } from "./providers/GameContext";
import { AuthProvider } from "./providers/AuthContext";
import logo from "./assets/Game_Materials/game_logo.png";
import FullscreenModal from "./fullscreenmodal/FullscreenModal";
import AdminRoute from "./Auth/AdminRoute";
import { PACKAGE_BUY_PATHS } from "./IframeViewer/frames/packageConfig";
import { PAYMENT_PATH } from "./IframeViewer/frames/tokenConfig";
import axiosInstance from "./utils/axiosConfig";
import SocialAppBrowserModal from "./components/SocialAppBrowserModal";
import { isSocialAppBrowser } from "./utils/isSocialAppBrowser";
const Login = lazy(() => import("./Auth/Login/Login"));
const Signup = lazy(() => import("./Auth/Signup/Signup"));
const AuthLayout = lazy(() => import("./Auth/AuthLayout/AuthLayout"));
const ForgotPassword = lazy(() => import("./Auth/ForgotPassword/ForgotPassword"));
const UnsubscribePromotional = lazy(() =>
  import("./Auth/UnsubscribePromotional/UnsubscribePromotional")
);
const Dashboard = lazy(() => import("./dashboard"));
const GalleryAdmin = lazy(() => import("./galleryAdmin"));
const IframeViewer = lazy(() => import("./IframeViewer"));
const PackageBuyLayout = lazy(() => import("./Auth/PackageBuyLayout/PackageBuyLayout"));
const WelcomePackage = lazy(() =>
  import("./IframeViewer/frames").then((module) => ({ default: module.WelcomePackage }))
);
const BonusPackage = lazy(() =>
  import("./IframeViewer/frames").then((module) => ({ default: module.BonusPackage }))
);
const WisePackage = lazy(() =>
  import("./IframeViewer/frames").then((module) => ({ default: module.WisePackage }))
);
const StartPackage = lazy(() =>
  import("./IframeViewer/frames").then((module) => ({ default: module.StartPackage }))
);
const PaymentPage = lazy(() => import("./IframeViewer/frames/PaymentPage"));
const PaymentRedirectPage = lazy(() => import("./IframeViewer/frames/PaymentRedirectPage"));
const PaymentFailedPage = lazy(() => import("./IframeViewer/frames/PaymentFailedPage"));
const GameLayoutWrapper = lazy(() => import("./GameLayout/GameLayoutWrapper"));
const GuessHintPreview = lazy(() => import("./Board/GuessHintPreview"));

const SERVER_DOMAIN = process.env.REACT_APP_BACKEND_HOST;

const AppContent = () => {
  const location = useLocation();
  const { setUserInfo } = useUser();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true); // Track auth check status
  const [isLandscape, setIsLandscape] = useState(
    window.innerWidth / window.innerHeight > 1.3
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Check if current route is an iframe route (public, no auth required)
  const isIframeRoute = location.pathname.startsWith('/iframe');
  // Package buy routes - page loads then package components redirect to login if needed
  const isBuyRoute = location.pathname.startsWith('/buy');
  const isUnsubscribeRoute = location.pathname.startsWith('/unsubscribe-promotional');
  const isGuessHintPreviewRoute = location.pathname.startsWith('/guess-hint-preview');

  const goFullScreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  };

  const exitFullScreen = () => {
    if (document.fullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia(
      "(max-width: 950px) and (orientation: landscape)"
    );

    const handleFullscreenChange = () => {
      if (mediaQuery.matches && isLandscape) {
        // Show custom modal when the media query is met
        setIsModalOpen(true);
      } else {
        setIsModalOpen(false);
        exitFullScreen();
      }
    };

    // Immediately check the condition on page load
    handleFullscreenChange();

    // Listen for changes in the media query
    mediaQuery.addEventListener("change", handleFullscreenChange);

    // Clean up on unmount
    return () => {
      mediaQuery.removeEventListener("change", handleFullscreenChange);
    };
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleModalConfirm = () => {
    goFullScreen();
    setIsModalOpen(false); // Close the modal after confirming fullscreen
  };

  const handleModalCancel = () => {
    setIsModalOpen(false); // Close the modal if the user declines
  };

  // Check authentication status via server using cookie
  // Skip auth check for iframe routes (they are public)
  useEffect(() => {
    // If this is an iframe or buy route, skip global auth check (buy handles its own auth)
    if (isIframeRoute || isBuyRoute || isUnsubscribeRoute || isGuessHintPreviewRoute) {
      setIsCheckingAuth(false);
      return;
    }

    const checkAuthStatus = async () => {
      try {
        // Validate authentication by calling /auth/me endpoint
        // Server reads token from HttpOnly cookie automatically
        console.log('Checking authentication status with server...');
        const response = await axiosInstance.get('/auth/me');

        // If we get user data, user is authenticated
        if (response.data.email) {
          console.log('User is authenticated');
          setIsAuthenticated(true);
          // Save user data to UserContext
          setUserInfo(response.data);
        } else {
          console.log('No user data returned, user not authenticated');
          setIsAuthenticated(false);
          setUserInfo(null);
        }
      } catch (error) {
        // Not authenticated or token expired
        console.log('Authentication check failed:', error.message);
        setIsAuthenticated(false);
        setUserInfo(null);
        // Clear any localStorage data
        localStorage.removeItem('userInfo');
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuthStatus();
  }, [isIframeRoute, isBuyRoute, isUnsubscribeRoute, isGuessHintPreviewRoute]);

  // Listen for window resize to detect orientation change
  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth / window.innerHeight > 1.3);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Show loading while checking authentication status
  // Skip loading screen for iframe and buy routes
  if (isCheckingAuth && !isIframeRoute && !isBuyRoute && !isUnsubscribeRoute && !isGuessHintPreviewRoute) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-black">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <Suspense fallback={<div className="text-white text-xl p-4">Loading page...</div>}>
      <Routes>
        <Route path="/" element={<AuthLayout />}>
          <Route
            path="/login"
            element={<Login onLoginSuccess={handleLoginSuccess} />}
          />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
        </Route>
        <Route
          path="/board"
          element={<GameLayoutWrapper isLandscape={isLandscape} />}
        />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route
          path="/galleryAdmin"
          element={
            <AdminRoute>
              <GalleryAdmin />
            </AdminRoute>
          }
        />
        <Route path="/iframe/*" element={<IframeViewer />} />
        <Route path="/iframe" element={<IframeViewer />} />
        <Route path={"/payment-redirect"} element={<PaymentRedirectPage />} />
        <Route path="/payment-failed" element={<PaymentFailedPage />} />
        <Route path="/unsubscribe-promotional" element={<UnsubscribePromotional />} />
        <Route path="/guess-hint-preview" element={<GuessHintPreview />} />
        <Route path="/buy" element={<PackageBuyLayout />}>
          <Route path="welcome" element={<WelcomePackage />} />
          <Route path="bonus" element={<BonusPackage />} />
          <Route path="wise" element={<WisePackage />} />
          <Route path="start" element={<StartPackage />} />
          <Route path={PAYMENT_PATH} element={<PaymentPage />} />
          <Route index element={<Navigate to="/board" replace />} />
        </Route>
        <Route
          path="*"
          element={
            <Navigate to={isAuthenticated ? "/board" : "/signup"} replace />
          }
        />
      </Routes>
    </Suspense>
  );
};

const App = () => {


  return (
    <AuthProvider>
      <UserProvider>
        <Router>

          <AppContent />
        </Router>
      </UserProvider>
    </AuthProvider>
  );
};

export default App;
